import React, { useEffect, useState } from 'react';
import { Container, Header } from './meal.styles';
import { useDispatch, useSelector } from 'react-redux';
import { get_export, get_meals, get_weekly_preference } from './redux/reducer';
import {
  getExportData,
  getLoading,
  getMealPreference,
  getMeals,
  getPageStats
} from './redux/selector';
import MealCard from './components/mealCard';
import Pagination from '@app/components/pagination';
import SearchContent from './components/search';
import useDebounce from '@app/Hooks/useDebounce';
import LoadingDataUi from '@app/components/loading';
import { Link } from 'react-router-dom';
import { getUser } from '@app/auth/redux/selectors';
import { getTags } from '../meal-tags/redux/selectors';
import { get_tags } from '../meal-tags/redux/reducers';
import BulkUpload from './bulk-upload/bulk-upload';

const MEAL_FILTERS_KEY = 'meal_filters';

const defaultFilters = {
  page: 1,
  limit: 10,
  type: null,
  field: null,
  sortBy: null,
  shown: null,
  hasTags: false,
  selectedTags: []
};

const { Parser } = require('json2csv');
const fileDownload = require('js-file-download');

const fields = [
  'Name',
  'Description',
  'Image',
  'Vegan',
  'Type',
  'Countries',
  'Ingredients',
  'Instructions',
  'ingredientsList'
];

const others = ['Ingredients'];
const meals_tags = ['Tag', 'Name', 'Category'];
const meal_ids = ['Meal ID', 'Name'];
const meal_and_tags = ['Meal', 'Category', 'Tag Name', 'TagID'];
const macros = [
  'Meal',
  'Carbohydrate',
  'Protein',
  'Fat',
  'Total Calories',
  'Calories Per Serving',
  'Servings'
];

const json2csvParser = new Parser({ fields });
const json2csvParsers = new Parser({ others });
const mealTagsParser = new Parser({ meals_tags });
const mealIdParser = new Parser({ meal_ids });
const mealNameTagParser = new Parser({ meal_and_tags });
const macrosParser = new Parser({ macros });

const groupMealsByTags = (meals) => {
  const grouped = {};

  meals?.forEach((meal) => {
    meal?.tags?.forEach((tag) => {
      if (!grouped[tag.name]) {
        grouped[tag.name] = [];
      }
      grouped[tag.name].push(meal);
    });
  });

  return grouped;
};

const Meals = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(40);
  const dispatch = useDispatch();
  const meals = useSelector(getMeals);
  const loading = useSelector(getLoading);
  const pageStats = useSelector(getPageStats);
  const [searchString, setSearchString] = useState('');
  const debouncedTerm = useDebounce(searchString, 500);
  const exportData = useSelector(getExportData);
  const user = useSelector(getUser);
  const fetching = useSelector(getMealPreference);
  const tags = useSelector(getTags);
  const [filters, setFilters] = useState(() => {
    const stored = localStorage.getItem(MEAL_FILTERS_KEY);
    return stored ? JSON.parse(stored) : defaultFilters;
  });
  const [hide, setHide] = useState(false);

  useEffect(() => {
    dispatch(get_tags());
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem(MEAL_FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    dispatch(
      get_meals({
        ...filters,
        searchString: debouncedTerm
      })
    );
  }, [dispatch, filters, debouncedTerm]);

  useEffect(() => {
    if (pageStats?.docs) {
      if (filters?.limit) delete filters.limit;
      dispatch(
        get_export({
          page: 1,
          limit: pageStats?.docs,
          ...filters
        })
      );
    }
  }, [dispatch, pageStats, filters]);

  const getNewMeals = (page, limit) => {
    dispatch(
      get_meals({
        ...filters,
        page: page,
        limit,
        searchString: debouncedTerm
      })
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getNewMeals(page, pageSize);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(parseInt(size));
    setCurrentPage(1);
    getNewMeals(1, size);
  };

  const handleSearch = (e) => {
    setSearchString(e.target.value);
  };

  // console.log(exportData)

  const exportMeals = () => {
    let temp = [];
    exportData.forEach((meal) => {
      // Handle new instruction_steps structure (array of objects) or fallback to old instructions (array of strings)
      let instructionsText = '';
      if (meal?.instruction_steps?.length > 0) {
        instructionsText = meal.instruction_steps
          .map((step, idx) => {
            const text = typeof step === 'object' ? step.step : step;
            return text ? `${idx + 1}. ${text}` : null;
          })
          .filter(Boolean)
          .join(' ');
      } else if (meal?.instructions?.length > 0) {
        instructionsText = meal.instructions
          .map((step, idx) => `${idx + 1}. ${step}`)
          .join(' ');
      }

      let obj = {
        Name: meal?.name,
        Description: meal?.description,
        Image: meal?.image,
        Vegan: meal?.vegan ? 'Yes' : 'No',
        Type: meal?.types?.join(', '),
        Countries: meal?.countries?.join(', '),
        Ingredients: meal?.ingredients?.join(', '),
        Instructions: instructionsText,
        ingredientsList: meal?.ingredients_list?.length > 0 ? true : false
      };
      temp.push(obj);
    });

    const csv = json2csvParser.parse(temp);
    fileDownload(csv, 'Meals.csv');
  };

  const exportByMealIds = () => {
    let temp = [];
    exportData.forEach((meal) => {
      let obj = {
        'Meal ID': meal?._id,
        Name: meal?.name
      };
      temp.push(obj);
    });

    const csv = mealIdParser.parse(temp);
    fileDownload(csv, 'Meals_IDs.csv');
  };

  const exportMealsAndTags = () => {
    let temp = [];

    exportData.forEach((meal) => {
      let obj = {
        Meal: meal?.name,
        Category: meal?.tags?.map((t) => `${t.category}`).join(', '),
        'Tag Name': meal?.tags?.map((t) => `${t.name}`).join(', '),
        TagID: meal?.tags?.map((t) => `${t._id}`).join(', ')
      };
      temp.push(obj);
    });
    const csv = mealNameTagParser.parse(temp);
    fileDownload(csv, 'Meals and Tags.csv');
  };

  const exportMealsByTags = () => {
    let temp = [];
    const groupedMeals = groupMealsByTags(exportData);

    Object.entries(groupedMeals).forEach(([tag, meals]) => {
      meals.forEach((meal) => {
        let obj = {
          Tag: tag,
          Name: meal?.name,
          Category: meal?.tags?.map((t) => `(${t.category})`).join(', ')
        };
        temp.push(obj);
      });
    });
    const csv = mealTagsParser.parse(temp);
    fileDownload(csv, 'Meals_By_Tags.csv');
  };

  const exportMealsbyMacros = () => {
    let temp = [];

    exportData.forEach((meal) => {
      let obj = {
        Meal: meal?.name,
        Carbohydrate: meal?.carbohydrate,
        Protein: meal?.protein,
        Fat: meal?.fat,
        'Total Calories': meal?.total_calories,
        'Calories Per Serving': meal?.calories_per_serving,
        Servings: meal?.servings
      };
      temp.push(obj);
    });
    const csv = macrosParser.parse(temp);
    fileDownload(csv, 'MealsMacros.csv');
  };

  const exportIngredients = () => {
    let allIngredients = new Set();

    exportData.forEach((meal) => {
      if (meal.ingredients && Array.isArray(meal.ingredients)) {
        meal.ingredients.forEach((ingredient) => {
          allIngredients.add(ingredient.toLowerCase().trim());
        });
      }
    });

    const temp = [];

    const uniqueIngredients = [...allIngredients];

    uniqueIngredients.forEach((data) => {
      let obj = {
        Ingredients: data
      };
      temp.push(obj);
    });

    const csv = json2csvParsers.parse(temp);
    fileDownload(csv, 'Ingredients.csv');
  };

  const getWeeklyPreference = () => {
    dispatch(get_weekly_preference());
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  return (
    <div>
      <BulkUpload isOpen={hide} meals={exportData} onClose={() => setHide(false)} />
      <Header>
        <div className="title"></div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="bg-[#549a9a] text-white text-[16px] font-[600] p-3 rounded-sm cursor-pointer"
            onClick={() => setHide(true)}
          >
            Upload Meals
          </button>

          <Link to="/meals/create/new">
            <button>Add Meal</button>
          </Link>

          {user?.email === 'richard@useakaani.com' && (
            <button onClick={getWeeklyPreference}>
              {fetching ? 'Getting...' : 'Get Weekly Preference'}
            </button>
          )}
          <button className="disabled:opacity-35">
            <Link to="/weekly-preference">View Weekly Meal Preferences</Link>
          </button>
        </div>
      </Header>

      <Container>
        <div className="content">
          <SearchContent
            handleFilterChange={handleFilterChange}
            handleSearch={handleSearch}
            exportMeal={exportMeals}
            tags={tags}
            filters={filters}
            exportMealsbyMacros={exportMealsbyMacros}
            exportMealsAndTags={exportMealsAndTags}
            exportByMealIds={exportByMealIds}
            exportMealsByTags={exportMealsByTags}
            exportIngredients={exportIngredients}
          />
        </div>
        {loading ? (
          <LoadingDataUi />
        ) : (
          <div>
            <div className="grid">
              {meals.map((meal) => (
                <MealCard
                  key={meal?._id}
                  id={meal._id}
                  title={meal.name}
                  type={meal.types}
                  countries={meal.countries}
                  meal={meal}
                />
              ))}
            </div>

            <div>
              {!loading && (
                <Pagination
                  itemsCount={pageStats.docs}
                  pageSize={pageSize}
                  currentPage={Number(currentPage)}
                  onChangePageSize={handlePageSizeChange}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Meals;
